<script setup>
/**
 * OCHAT: los días de la semana a los que se aplica una carga por rango.
 *
 * Es la pieza que convierte "cargar una temporada" en una sola operación: "del 1/12
 * al 28/02, solo viernes y sábados, 95.000". Sin esto habría que cargar cada fin de
 * semana por separado, que es el 80% del trabajo real de un hotelero.
 *
 * NINGUNO ELEGIDO SIGNIFICA TODOS
 * Es la misma regla que aplica el servicio del servidor
 * (Bookings::BulkCalendarService#normalize_weekdays), y es lo que espera quien carga
 * una temporada completa y no toca este control. Se dice con palabras abajo del
 * control para que no haya que adivinarlo.
 *
 * Los números son los de Date#wday: 0 es domingo. Se muestran empezando por el lunes
 * porque así se lee un calendario en español, pero el valor que viaja es el de wday.
 */
import { computed } from 'vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

// [wday, clave de i18n]. El orden es de visualización, no el de wday.
const DAYS = [
  [1, 'MON'],
  [2, 'TUE'],
  [3, 'WED'],
  [4, 'THU'],
  [5, 'FRI'],
  [6, 'SAT'],
  [0, 'SUN'],
];

const selected = computed(() => props.modelValue ?? []);
const allDays = computed(() => selected.value.length === 0);

const toggle = wday => {
  const next = selected.value.includes(wday)
    ? selected.value.filter(day => day !== wday)
    : [...selected.value, wday];
  emit('update:modelValue', next);
};

const clear = () => emit('update:modelValue', []);

const selectWeekend = () => emit('update:modelValue', [5, 6]);
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex flex-wrap gap-1">
      <button
        v-for="[wday, key] in DAYS"
        :key="wday"
        type="button"
        :aria-pressed="selected.includes(wday)"
        class="w-10 py-1.5 text-sm font-medium rounded-lg outline outline-1 outline-n-container text-n-slate-11"
        :class="
          selected.includes(wday)
            ? '!outline-n-brand bg-n-brand text-white'
            : 'bg-n-background hover:text-n-slate-12'
        "
        @click="toggle(wday)"
      >
        {{ $t(`BOOKINGS.SETTINGS.WEEKDAYS.${key}`) }}
      </button>
    </div>

    <div class="flex flex-wrap items-center gap-3 text-sm">
      <button
        type="button"
        class="text-n-brand hover:underline"
        @click="selectWeekend"
      >
        {{ $t('BOOKINGS.SETTINGS.WEEKDAYS.WEEKEND') }}
      </button>
      <button
        v-if="!allDays"
        type="button"
        class="text-n-slate-11 hover:underline"
        @click="clear"
      >
        {{ $t('BOOKINGS.SETTINGS.WEEKDAYS.CLEAR') }}
      </button>
      <span v-if="allDays" class="text-n-slate-11">
        {{ $t('BOOKINGS.SETTINGS.WEEKDAYS.ALL_HINT') }}
      </span>
    </div>
  </div>
</template>
